/**
 * Integrations Management
 * Connect and manage accounting system integrations (Bexio, Xero, Banana, CAMT)
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import {
  Plug,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Unplug,
  ExternalLink,
  FileSpreadsheet,
  Building2,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";

type IntegrationStatus =
  | "connected"
  | "disconnected"
  | "expired"
  | "error"
  | "not_configured";

function StatusBadge({ status }: { status: IntegrationStatus }) {
  switch (status) {
    case "connected":
      return (
        <Badge
          variant="outline"
          className="border-[var(--success)] text-[var(--success)]"
        >
          <CheckCircle className="mr-1 h-3 w-3" />
          Connected
        </Badge>
      );
    case "expired":
      return (
        <Badge
          variant="outline"
          className="border-[var(--warning)] text-[var(--warning)]"
        >
          <Clock className="mr-1 h-3 w-3" />
          Expired
        </Badge>
      );
    case "error":
      return (
        <Badge variant="destructive">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Error
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          <XCircle className="mr-1 h-3 w-3" />
          Not Connected
        </Badge>
      );
  }
}

function IntegrationIcon({ id }: { id: string }) {
  switch (id) {
    case "bexio":
    case "xero":
      return <Building2 className="h-8 w-8 text-[var(--primary)]" />;
    case "banana":
      return <FileSpreadsheet className="h-8 w-8 text-[var(--primary)]" />;
    case "camt":
    case "mt940":
      return <Landmark className="h-8 w-8 text-[var(--primary)]" />;
    default:
      return <Plug className="h-8 w-8 text-[var(--primary)]" />;
  }
}

function formatDate(timestamp: number | null): string {
  if (!timestamp) return "Never";
  return new Date(timestamp).toLocaleString("de-CH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function Integrations() {
  const { selectedCompanyId } = useCompany();
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const availableQuery = trpc.integration.list.useQuery();
  const connectedQuery = trpc.integration.listIntegrations.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const bexioStatusQuery = trpc.integration.bexioStatus.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const xeroStatusQuery = trpc.integration.xeroStatus.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const bexioDisconnect = trpc.integration.bexioDisconnect.useMutation({
    onSuccess: () => {
      toast.success("Bexio disconnected");
      connectedQuery.refetch();
      bexioStatusQuery.refetch();
    },
    onError: err => toast.error(err.message),
  });

  const xeroDisconnect = trpc.integration.xeroDisconnect.useMutation({
    onSuccess: () => {
      toast.success("Xero disconnected");
      connectedQuery.refetch();
      xeroStatusQuery.refetch();
    },
    onError: err => toast.error(err.message),
  });

  const bexioSync = trpc.integration.bexioSync.useMutation({
    onSuccess: data => {
      toast.success(
        `Bexio sync complete: ${data.syncResult.imported} imported, ${data.syncResult.updated} updated`
      );
      connectedQuery.refetch();
      bexioStatusQuery.refetch();
      setSyncingId(null);
    },
    onError: err => {
      toast.error(`Bexio sync failed: ${err.message}`);
      setSyncingId(null);
    },
  });

  const xeroSync = trpc.integration.xeroSync.useMutation({
    onSuccess: data => {
      toast.success(
        `Xero sync complete: ${data.syncResult.imported} imported, ${data.syncResult.updated} updated`
      );
      connectedQuery.refetch();
      xeroStatusQuery.refetch();
      setSyncingId(null);
    },
    onError: err => {
      toast.error(`Xero sync failed: ${err.message}`);
      setSyncingId(null);
    },
  });

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <Alert>
          <AlertDescription>
            Please select a company to manage integrations.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const available = availableQuery.data?.integrations ?? [];
  const connected = connectedQuery.data?.integrations ?? [];
  const isLoading = availableQuery.isLoading || connectedQuery.isLoading;

  const apiIntegrations = available.filter(
    (i: { type: string }) => i.type === "api"
  );
  const fileIntegrations = available.filter(
    (i: { type: string }) => i.type === "file"
  );

  function getConnectionStatus(providerId: string): IntegrationStatus {
    if (providerId === "bexio" && bexioStatusQuery.data) {
      return bexioStatusQuery.data.connected
        ? "connected"
        : (bexioStatusQuery.data.status as IntegrationStatus);
    }
    if (providerId === "xero" && xeroStatusQuery.data) {
      return xeroStatusQuery.data.connected
        ? "connected"
        : (xeroStatusQuery.data.status as IntegrationStatus);
    }
    const conn = connected.find(c => c.provider === providerId);
    return conn?.connected ? "connected" : "not_configured";
  }

  function getLastSync(providerId: string): number | null {
    const conn = connected.find(c => c.provider === providerId);
    return conn?.lastSyncAt ?? null;
  }

  function handleConnect(providerId: string) {
    // For OAuth integrations, we'd typically redirect to the OAuth flow
    // For now, show a placeholder toast
    toast.info(
      `To connect ${providerId}, configure OAuth credentials in Settings > Integrations.`
    );
  }

  function handleSync(providerId: string) {
    if (!selectedCompanyId) return;
    setSyncingId(providerId);

    if (providerId === "bexio") {
      bexioSync.mutate({
        companyId: selectedCompanyId,
        direction: "import",
      });
    } else if (providerId === "xero") {
      xeroSync.mutate({
        companyId: selectedCompanyId,
        direction: "import",
      });
    }
  }

  function handleDisconnect(providerId: string) {
    if (!selectedCompanyId) return;

    if (providerId === "bexio") {
      bexioDisconnect.mutate({ companyId: selectedCompanyId });
    } else if (providerId === "xero") {
      xeroDisconnect.mutate({ companyId: selectedCompanyId });
    }
    setDisconnectingId(null);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Integrations</h1>
          <p className="text-muted-foreground">
            Connect your accounting tools and bank statement formats.
          </p>
        </div>

        {/* API Integrations (OAuth) */}
        <div>
          <h2 className="mb-3 text-lg font-semibold">Cloud Integrations</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading
              ? Array.from({ length: 2 }).map((_, i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-8 w-8 rounded" />
                      <Skeleton className="mt-2 h-5 w-32" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))
              : apiIntegrations.map(integration => {
                  const status = getConnectionStatus(integration.id);
                  const lastSync = getLastSync(integration.id);
                  const isConnected = status === "connected";
                  const isSyncing = syncingId === integration.id;

                  return (
                    <Card key={integration.id}>
                      <CardHeader className="flex flex-row items-start justify-between space-y-0">
                        <div className="flex items-center gap-3">
                          <IntegrationIcon id={integration.id} />
                          <div>
                            <CardTitle className="text-base">
                              {integration.name}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {integration.description}
                            </CardDescription>
                          </div>
                        </div>
                        <StatusBadge status={status} />
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isConnected && (
                          <div className="text-xs text-muted-foreground">
                            Last synced: {formatDate(lastSync)}
                          </div>
                        )}
                        <div className="flex gap-2">
                          {isConnected ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSync(integration.id)}
                                disabled={isSyncing}
                              >
                                <RefreshCw
                                  className={`mr-1 h-3 w-3 ${isSyncing ? "animate-spin" : ""}`}
                                />
                                {isSyncing ? "Syncing..." : "Sync Now"}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() =>
                                  setDisconnectingId(integration.id)
                                }
                              >
                                <Unplug className="mr-1 h-3 w-3" />
                                Disconnect
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleConnect(integration.id)}
                            >
                              <ExternalLink className="mr-1 h-3 w-3" />
                              Connect
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
          </div>
        </div>

        {/* File-based Integrations */}
        <div>
          <h2 className="mb-3 text-lg font-semibold">File Formats & Imports</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-5 w-32" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))
              : fileIntegrations.map(integration => (
                  <Card key={integration.id}>
                    <CardHeader className="flex flex-row items-start gap-3 space-y-0">
                      <IntegrationIcon id={integration.id} />
                      <div>
                        <CardTitle className="text-base">
                          {integration.name}
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {integration.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1">
                        {integration.importFormats.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            Import:{" "}
                            {integration.importFormats
                              .map(f => f.toUpperCase())
                              .join(", ")}
                          </Badge>
                        )}
                        {integration.exportFormats.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            Export:{" "}
                            {integration.exportFormats
                              .map(f => f.toUpperCase())
                              .join(", ")}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
          </div>
        </div>

        {/* Disconnect Confirmation Dialog */}
        <AlertDialog
          open={!!disconnectingId}
          onOpenChange={() => setDisconnectingId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect Integration</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the connection to{" "}
                {disconnectingId?.charAt(0).toUpperCase()}
                {disconnectingId?.slice(1)}. Synced data will be preserved, but
                automatic syncing will stop.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() =>
                  disconnectingId && handleDisconnect(disconnectingId)
                }
              >
                Disconnect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
